<script setup>
import {
    convertPriorityToColor,
    PoliceVerificationButtonLabel,
    PriorityLabel
} from "@/components/hpo/choices";
import {GenderLabel, IsChildLabel} from "@/choices";
import WizardSummaryField from "@/components/wizard/WizardSummaryField.vue";
import WizardSummary from "@/components/wizard/WizardSummary.vue";
import {computed} from "vue";
import dayjs from "dayjs";
import {pgettext} from "@/utils/translate";
import {insuranceChoices} from "@/components/hpo/identification/insurance";
import {mdiPencil} from "@mdi/js";

const props = defineProps({
    patientId: {
        type: [Number, String],
    },
    formData: {
        type: Object,
        required: true,
    },
    isSubmitting: {
        type: Boolean,
        default: false,
    },
    countries: {
        type: Array,
        required: true,
    },
});
const emit = defineEmits(['pushStep', 'submit']);

const dateOfBirthFormatted = computed(() => {
    if (!props.formData.birthDay && !props.formData.birthMonth && !props.formData.birthYear) {
        return '';
    }

    if (!props.formData.birthDay && !props.formData.birthMonth) {
        return `${props.formData.birthYear}`;
    }

    if (!props.formData.birthDay) {
        return dayjs(
            new Date(props.formData.birthYear, props.formData.birthMonth - 1, 1)
        ).format('M. YYYY');
    }

    return dayjs(
        new Date(props.formData.birthYear, props.formData.birthMonth - 1, props.formData.birthDay)
    ).format('D. M. YYYY');
});

function countryName(code) {
    if (!code) {
        return null;
    }

    for (const country of props.countries) {
        if (country.code === code) {
            return country.name;
        }
    }
    return code;
}

function goToStep(stepName) {
    emit('pushStep', stepName);
}

function submit() {
    emit('submit', props.formData);
}

function isDefined(value) {
    return value !== null && value !== undefined;
}

function getInsuranceLabel(insurance) {
    for (const {code, name} of insuranceChoices) {
        if (insurance === parseInt(code)) {
            return name;
        }
    }
    return '';
}
</script>

<template>
    <main>
        <h1>{{ pgettext('title', 'Summary') }}</h1>

        <div class="text-center mb-4">
            <v-btn @click="goToStep('identification')" :prepend-icon="mdiPencil">
                {{ pgettext('action', 'Edit') }}
            </v-btn>
        </div>
        <div class="d-flex justify-center">
            <WizardSummary vertical>
                <WizardSummaryField :title="pgettext('person_name', 'Name')" disableEdit>
                    {{ formData.firstName }} <strong>{{ formData.lastName }}</strong>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Country')" disableEdit>
                    <template v-if="formData.country">
                        {{ countryName(formData.country) }}
                    </template>
                    <!-- A recorded answer, so it is rendered as real content rather than
                         the em-dash placeholder used for "nothing here". -->
                    <template v-else-if="formData.countryUnknown === true">
                        {{ pgettext('action', 'Cannot be determined') }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'TIC')" disableEdit>
                    <template v-if="formData.ticPrefix && formData.ticNumber">
                        {{ formData.ticPrefix }}{{ formData.ticNumber }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Gender')" disableEdit>
                    <template v-if="isDefined(formData.gender)">
                        {{ GenderLabel[formData.gender] }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Age')" disableEdit>
                    <template v-if="isDefined(formData.isChild)">
                        {{ IsChildLabel[formData.isChild] }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField v-if="isDefined(formData.treatmentPriority)" :title="pgettext('label', 'Treatment Priority')" disableEdit>
                    <v-chip density="compact" variant="flat" size="large" :ripple="false"
                            :color="convertPriorityToColor(formData.treatmentPriority).toLowerCase()" rounded class="py-0 px-5">
                        <strong>{{ PriorityLabel[formData.treatmentPriority] }}</strong>
                    </v-chip>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Date of Birth')" disableEdit>
                    <template v-if="dateOfBirthFormatted">
                        {{ dateOfBirthFormatted }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Birth number')" disableEdit>
                    <template v-if="isDefined(formData.birthNumber)">
                        {{ formData.birthNumber }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Insurance')" disableEdit>
                    <template v-if="isDefined(formData.insurance)">
                        {{ getInsuranceLabel(formData.insurance) }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Address')" disableEdit>
                    <template v-if="isDefined(formData.address)">
                        {{ formData.address }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'City')" disableEdit>
                    <template v-if="isDefined(formData.city)">
                        {{ formData.city }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Phone number')" disableEdit>
                    <template v-if="isDefined(formData.phoneNumber)">
                        {{ formData.phoneNumber }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Note')" disableEdit>
                    <template v-if="isDefined(formData.note)">
                        {{ formData.note }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
                <WizardSummaryField :title="pgettext('label', 'Police verification')" disableEdit>
                    <template v-if="isDefined(formData.policeVerification)">
                        {{ PoliceVerificationButtonLabel[formData.policeVerification] }}
                    </template>
                    <em v-else>&ndash;</em>
                </WizardSummaryField>
            </WizardSummary>
        </div>

        <div class="text-center mt-6">
            <v-btn class="px-8" @click="submit()" color="green" size="large" :loading="isSubmitting">
                <strong>{{ pgettext('action', 'Save') }}</strong>
            </v-btn>
        </div>
    </main>
</template>
